import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";

export const PROJECT_STATUS_OPTIONS = [
  { value: "active", label: "Aktiv", color: "bg-[#D9F5E8] text-[#035C39]", dot: "bg-success" },
  { value: "kommende", label: "Kommende", color: "bg-[var(--sr-reveblaa)] text-white", dot: "bg-white" },
  { value: "completed", label: "Ferdig", color: "bg-card text-foreground", dot: "bg-foreground/40" },
  { value: "cancelled", label: "Kansellert", color: "bg-[#FFE1DB] text-[#7F1D1D]", dot: "bg-destructive" },
];

const BADGE_BASE =
  "self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full border-[1.5px] border-foreground text-[12.5px] font-medium";

export function ProjectStatusBadge({
  status,
  onStatusChange,
  isAdmin = true,
}: {
  status: string | null;
  onStatusChange?: (newStatus: string) => void;
  /** When false, hides the "Kansellert" option. Defaults to true for backwards compat. */
  isAdmin?: boolean;
}) {
  const isClickable = !!onStatusChange;
  const statusOption = PROJECT_STATUS_OPTIONS.find((opt) => opt.value === status);
  const displayLabel = statusOption?.label ?? "—";
  const colorClass = statusOption?.color ?? "bg-card text-foreground";
  const dotClass = statusOption?.dot ?? "bg-foreground/30";

  const content = (
    <>
      <span className={cn("w-2 h-2 rounded-full", dotClass)} aria-hidden />
      <span>{displayLabel}</span>
    </>
  );

  if (!isClickable) {
    return <span className={cn(BADGE_BASE, colorClass)}>{content}</span>;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            BADGE_BASE,
            colorClass,
            "cursor-pointer transition-shadow hover:shadow-[0_0_0_3px_rgba(3,30,255,0.15)]",
          )}
        >
          {content}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-2 border-[1.5px] border-foreground">
        <div className="space-y-1">
          {PROJECT_STATUS_OPTIONS.filter((option) => {
            // "Ferdig" settes kun via "Lukk prosjekt"-flyten i hamburger-
            // menyen så closing-checklist (BF, Clean 4:2:2, etc.) blir
            // bekreftet. Status-badgen kan IKKE sette completed direkte.
            // Hvis prosjektet allerede er completed, kan brukeren velge
            // en annen status fra dropdownen for å åpne det igjen.
            if (option.value === "completed") return false;
            if (option.value === "cancelled" && !isAdmin) return false;
            return true;
          }).map((option) => (
            <button
              key={option.value}
              onClick={() => onStatusChange(option.value)}
              className={cn(
                "w-full text-left px-3 py-2 rounded text-sm transition-colors hover:bg-muted",
                "flex items-center gap-2",
                option.value === status && "bg-muted",
              )}
            >
              <span className={cn("w-2.5 h-2.5 rounded-full", option.dot)} aria-hidden />
              <span className="flex-1">{option.label}</span>
              {option.value === status && <SrIcon name="check" className="w-4 h-4 text-primary" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ProjectStatusBadge;
